import { useMemo, useEffect, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { FormGroupHeader, SwitchInputField } from "@/components/shared";
import { Form } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { CircleLoader } from "@/components/shared/loaders";
import useFetchNotificationPreferencesQueryHook from "@/hooks/queries/useFetchNotificationPreferencesQuery";
import useUpdateNotificationPreferencesMutationHook from "@/hooks/mutations/useUpdateNotificationSettings";
import { isManager } from "@/utils/authentication";

const dailySummarySectionsSchema = z.object({
  bidDecisions: z.boolean().default(false),
  marginDecisions: z.boolean().default(false),
  tasks: z.boolean().default(false),
});

// We relax the validation schema here because we handle the logic
// (e.g., auto-disabling if no days selected) programmatically
// before sending data to the API.
const createFormSchema = () => {
  return z.object({
    monday: z.boolean(),
    tuesday: z.boolean(),
    wednesday: z.boolean(),
    thursday: z.boolean(),
    friday: z.boolean(),
    dailySummarySections: dailySummarySectionsSchema,
  });
};

type NotificationPreferencesFormData = z.infer<ReturnType<typeof createFormSchema>>;

const DAYS_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday"] as const;

/**
 * NotificationsComponent
 *
 * This component is used to display and manage the user's notification preferences.
 * It uses the useFetchNotificationPreferencesQueryHook and useUpdateNotificationPreferencesMutationHook
 * to fetch and update the notification preferences.
 *
 * It uses the useWatch hook to watch the values of the form and the useCallback hook to save the changes.
 *
 * It uses the useEffect hook to reset the form when the data arrives.
 *
 * @returns NotificationsComponent
 */
const NotificationsComponent = () => {

  // Hooks
  const { t } = useTranslation(["translation", "common", "settings"]);

  // GraphQL Hooks
  const { data: notificationPreferences, loading: queryLoading } =
    useFetchNotificationPreferencesQueryHook();

  const { updateNotificationPreferencesMutation, loading: updateLoading } =
    useUpdateNotificationPreferencesMutationHook();

  const initialValues = useMemo<NotificationPreferencesFormData>(() => {
    return {
      monday: notificationPreferences?.monday ?? false,
      tuesday: notificationPreferences?.tuesday ?? false,
      wednesday: notificationPreferences?.wednesday ?? false,
      thursday: notificationPreferences?.thursday ?? false,
      friday: notificationPreferences?.friday ?? false,
      dailySummarySections: {
        bidDecisions: notificationPreferences?.dailySummaryBidDecisions ?? false,
        marginDecisions: notificationPreferences?.dailySummaryMarginDecisions ?? false,
        tasks: notificationPreferences?.dailySummaryTasks ?? false,
      },
    };
  }, [notificationPreferences]);

  const form = useForm({
    resolver: zodResolver(createFormSchema()),
    defaultValues: initialValues,
  });

  // Reset form when data arrives
  useEffect(() => {
    if (notificationPreferences) {
      form.reset(initialValues);
    }
  }, [notificationPreferences, initialValues, form]);

  // Watch values for UI logic
  const watchedDays = useWatch({ control: form.control, name: DAYS_KEYS });

  /**
   * Central function to map form data to API input and fire mutation.
   * We pass specific overrides because React state/Form state might be
   * one tick behind inside the event handler.
   * If no days are selected, all sections are disabled.
   */
  const saveChanges = useCallback((overrides?: Partial<NotificationPreferencesFormData>) => {
    const currentValues = form.getValues();
    const data = { ...currentValues, ...overrides };

    // Check if any day is selected
    const hasAnyDaySelected =
      data.monday ||
      data.tuesday ||
      data.wednesday ||
      data.thursday ||
      data.friday;

    const input = {
      notificationDays: {
        monday: data.monday,
        tuesday: data.tuesday,
        wednesday: data.wednesday,
        thursday: data.thursday,
        friday: data.friday,
      },
      // If no days are selected, ensure sections are sent as false
      dailySummaryBidDecisions: hasAnyDaySelected ? (data.dailySummarySections.bidDecisions ?? false) : false,
      dailySummaryMarginDecisions: hasAnyDaySelected ? (data.dailySummarySections.marginDecisions ?? false) : false,
      dailySummaryTasks: hasAnyDaySelected ? (data.dailySummarySections.tasks ?? false) : false,
    };

    updateNotificationPreferencesMutation({
      variables: { input },
    });
  }, [form, updateNotificationPreferencesMutation]);

  /**
   * Handle Individual Day Toggle
   * - If all days are deselected, sections are automatically disabled
   */
  const handleDayToggle = (day: typeof DAYS_KEYS[number], checked: boolean) => {
    // Update the form value immediately
    form.setValue(day, checked);

    // Calculate new active count
    // We construct the new state manually to check logic before saving
    const currentDays = form.getValues();
    const updatedDays = { ...currentDays, [day]: checked };

    const activeCount = DAYS_KEYS.reduce((count, key) => {
      return count + (updatedDays[key] ? 1 : 0);
    }, 0);

    if (activeCount === 0) {
      // Logic: If no days left, disable all sections
      form.setValue("dailySummarySections.bidDecisions", false);
      form.setValue("dailySummarySections.marginDecisions", false);
      form.setValue("dailySummarySections.tasks", false);
      saveChanges({
        [day]: checked,
        dailySummarySections: { bidDecisions: false, marginDecisions: false, tasks: false }
      });
    } else {
      // Logic: Standard save
      saveChanges({ [day]: checked });
    }
  };

  /**
   * Handle "Select All" / "Deselect All"
   */
  const handleSelectAllDays = () => {
    // Determine if we are selecting all or deselecting all based on current state
    const currentDays = form.getValues();
    const allCurrentlySelected = DAYS_KEYS.every(day => currentDays[day]);
    const newState = !allCurrentlySelected;

    if (!newState) {
      // If deselecting all -> Disable all sections
      DAYS_KEYS.forEach(day => form.setValue(day, false));
      form.setValue("dailySummarySections.bidDecisions", false);
      form.setValue("dailySummarySections.marginDecisions", false);
      form.setValue("dailySummarySections.tasks", false);
      saveChanges({
        monday: false, tuesday: false, wednesday: false, thursday: false, friday: false,
        dailySummarySections: { bidDecisions: false, marginDecisions: false, tasks: false }
      });
    } else {
      // If selecting all -> Update all fields and save
      DAYS_KEYS.forEach(day => form.setValue(day, true));
      saveChanges({
        monday: true, tuesday: true, wednesday: true, thursday: true, friday: true
      });
    }
  };

  /**
   * Handle Section Toggles
   */
  const handleSectionToggle = (field: keyof typeof dailySummarySectionsSchema.shape, checked: boolean) => {
    const path = `dailySummarySections.${field}` as const;
    form.setValue(path, checked);

    // Construct the nested object for the saver
    const currentSections = form.getValues("dailySummarySections");
    saveChanges({ dailySummarySections: { ...currentSections, [field]: checked } as NotificationPreferencesFormData["dailySummarySections"] });
  };

  /**
   * Loading component
   */
  if (queryLoading && !notificationPreferences) {
    return (
      <div className="pb-[300px] flex items-center justify-center min-h-[400px]">
        <CircleLoader visible={true} height={80} width={80} strokeWidth={8} ariaLabel="loading" />
      </div>
    );
  }

  // Calculate if all days are selected for the "Select All" button state
  const activeDaysCount = watchedDays.filter(Boolean).length;
  const isAllSelected = activeDaysCount === 5;
  const hasAnyDaySelected = activeDaysCount > 0;

  return (
    <div className="pb-[300px]">
      <Form {...form}>
        <form>
          <FormGroupHeader
            className="pr-4 pl-2"
            title={t("settings.notifications.title")}
            description={t("settings.notifications.description")}
          />
          {/* Visual indicator for saving state could be added here if needed */}
          <div className="h-1 flex justify-end px-4">
            {updateLoading && <span className="text-xs text-muted-foreground animate-pulse">{t("settings.notifications.saving")}</span>}
          </div>

          <div className="pr-4 pl-2 mt-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("settings.notifications.dailysummaryemails")}
                </CardTitle>
                <CardDescription>
                  {t("settings.notifications.dailysummarydescription")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Separator className="mb-4" />
                {/* Days Selector */}
                <div>
                  <p className="text-sm font-medium mb-3">
                    {t("settings.notifications.notificationsdays")}
                  </p>

                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {DAYS_KEYS.map((day) => {
                        const isActive = form.getValues(day);
                        return (
                          <Button
                            key={day}
                            type="button"
                            variant="outline"
                            onClick={() => handleDayToggle(day, !isActive)}
                            className={cn(
                              "transition-all min-w-12",
                              isActive && "bg-slate-800 text-white hover:bg-slate-700 hover:text-white"
                            )}
                          >
                            {t(`common:weekdaysshort.${day}`)}
                          </Button>
                        );
                      })}

                      <Button
                        variant="ghost"
                        type="button"
                        onClick={handleSelectAllDays}
                      >
                        {isAllSelected ? t("common:deselectall") : t("common:selectall")}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Sections (Visible if Manager) */}
                {isManager() && (
                  <div className="pt-4">
                    <Separator className="mb-4" />
                    <p className="text-sm font-medium mb-3">
                      {t("settings.notifications.sections.title")}
                    </p>
                    <div className="space-y-4 pl-1">
                      <SwitchInputField
                        form={form}
                        name="dailySummarySections.bidDecisions"
                        label={t("settings.notifications.sections.bidDecisions")}
                        disabled={!hasAnyDaySelected}
                        onCheckedChange={(c) => handleSectionToggle("bidDecisions", c)}
                      />
                      <SwitchInputField
                        form={form}
                        name="dailySummarySections.marginDecisions"
                        label={t("settings.notifications.sections.marginDecisions")}
                        disabled={!hasAnyDaySelected}
                        onCheckedChange={(c) => handleSectionToggle("marginDecisions", c)}
                      />
                      <SwitchInputField
                        form={form}
                        name="dailySummarySections.tasks"
                        label={t("settings.notifications.sections.tasks")}
                        disabled={!hasAnyDaySelected}
                        onCheckedChange={(c) => handleSectionToggle("tasks", c)}
                      />
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default NotificationsComponent;
